import type { AvatarColor } from './avatar.types';

/** Ordered palette used by {@link getAvatarColor} to map a name hash to a color. */
const AVATAR_COLORS: AvatarColor[] = ['neutral', 'green', 'blue', 'yellow'];

/**
 * 2^31 − 1 (a Mersenne prime). Caps the polynomial hash state so it never
 * leaves the IEEE-754 safe-integer range during iteration.
 */
const HASH_MODULUS = 2_147_483_647;

/**
 * Fractional part of 1/φ (reciprocal of the golden ratio). Used for
 * Knuth-style multiplicative (Fibonacci) hashing: multiplying any integer
 * by an irrational fraction produces a value whose fractional part is
 * equidistributed on [0, 1), which gives a uniform projection onto the
 * palette regardless of how clustered the raw hash happens to be.
 */
const INVERSE_GOLDEN_RATIO = 0.618_033_988_749_895;

/**
 * Deterministically derives an {@link AvatarColor} from a name.
 *
 * Uses a polynomial rolling hash (base 31, reduced modulo a 31-bit prime)
 * followed by Fibonacci hashing to project the result onto the four-color
 * palette. The Fibonacci step is what makes the output evenly distributed —
 * a plain `hash % palette.length` would bias heavily toward one or two
 * colors for typical English names because only the low bits are sampled.
 *
 * The same input always yields the same color, which is useful for fallback
 * avatars where no image or explicit color is supplied.
 *
 * @example
 * <Avatar color={getAvatarColor(user.name)}>
 *   <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
 * </Avatar>
 */
export function getAvatarColor(name: string): AvatarColor {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) % HASH_MODULUS;
  }
  const fraction = (hash * INVERSE_GOLDEN_RATIO) % 1;
  return AVATAR_COLORS[Math.floor(fraction * AVATAR_COLORS.length)]!;
}
